import type { Article } from '@/mocks/articles';

const siteUrl = 'https://acbuyspreadsheet.living';
const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/acdcfd47829ab306d380ea84eb14e50b.jpeg';

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ACBuySpreadsheet',
  url: siteUrl,
  logo: logoUrl,
  description: 'The best ACBuy spreadsheet directory with curated product links, buying guides, and shopping resources.',
  foundingDate: '2026',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    availableLanguage: ['English'],
  },
};

export const homeWebPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'ACBuySpreadsheet – Best ACBuy Spreadsheet Directory 2026',
  description: 'Explore the latest ACBuySpreadsheet directory featuring 5000+ product links across 14 categories. Find curated ACBuy spreadsheets, buying guides, safety tips, and trusted shopping resources updated weekly for 2026.',
  url: `${siteUrl}/`,
  isPartOf: {
    '@type': 'WebSite',
    name: 'ACBuySpreadsheet',
    url: siteUrl,
  },
  about: {
    '@type': 'Organization',
    name: 'ACBuySpreadsheet',
    url: siteUrl,
  },
  primaryImageOfPage: {
    '@type': 'ImageObject',
    url: logoUrl,
  },
};

export const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is ACBuySpreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ACBuySpreadsheet is a dedicated directory platform that organizes and curates over 5,000 verified product links across 14 categories for the ACBuy shopping ecosystem. It serves as your personal shopping assistant — bringing together Shoes, Hoodies, T-Shirts, Jackets, Accessories, and more in one clean, organized interface.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is ACBuySpreadsheet safe to use?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Every link in the ACBuySpreadsheet directory is manually verified by our editorial team before being published. We conduct weekly audits to ensure all links remain active and point to legitimate product pages. The platform does not require registration or collect personal information.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does ACBuySpreadsheet work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ACBuySpreadsheet organizes product links into 14 curated categories. Users browse the category dropdown or grid on the homepage, select a category, and are directed to the corresponding verified product page on KASpreadsheet in a new tab. No registration, no fees — completely free access.',
      },
    },
    {
      '@type': 'Question',
      name: 'What products can I find on ACBuySpreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ACBuySpreadsheet covers 14 categories: Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear/Underpants, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category contains curated, verified product links updated weekly.',
      },
    },
    {
      '@type': 'Question',
      name: 'How often is ACBuySpreadsheet updated?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ACBuySpreadsheet is updated on a weekly basis. Our editorial team verifies all existing links and adds new product listings every week, ensuring you always have access to active, current product pages across all 14 categories.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need an account to use ACBuySpreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. ACBuySpreadsheet is completely free and does not require any registration or account creation. Simply visit the homepage, browse the categories, and start shopping — no sign-ups, no downloads, no technical knowledge required.',
      },
    },
  ],
};

export function buildGuideArticleSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Complete ACBuy Spreadsheet Guide 2026 — How to Use ACBuySpreadsheet Effectively',
    description: 'Everything you need to know about navigating the acbuy spreadsheet directory — from getting started to advanced shopping strategies. Covers all 14 categories, safety tips, and how to find the best acbuy spreadsheet deals in 2026.',
    image: 'https://readdy.ai/api/search-image?query=Modern%20workspace%20with%20laptop%20displaying%20clean%20dashboard%20interface%2C%20surrounded%20by%20fashion%20catalog%20items%20and%20coffee%20on%20warm%20oak%20desk%2C%20soft%20natural%20daylight%20from%20window%2C%20editorial%20photography%2C%20organized%20composition%2C%20warm%20neutral%20background%2C%20professional%20yet%20cozy%20atmosphere%2C%20no%20visible%20screen%20text&width=1400&height=600&seq=guide-article-hero-v2&orientation=landscape',
    author: {
      '@type': 'Organization',
      name: 'ACBuySpreadsheet Editorial Team',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'ACBuySpreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
      },
    },
    datePublished: '2026-06-01',
    dateModified: '2026-06-25',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/acbuy-spreadsheet-guide`,
    },
  };
}

export function buildGuideBreadcrumbSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Complete ACBuy Spreadsheet Guide 2026',
        item: `${siteUrl}/acbuy-spreadsheet-guide`,
      },
    ],
  };
}

export function buildArticleSchema(article: Article): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Organization',
      name: 'ACBuySpreadsheet Editorial Team',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'ACBuySpreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
      },
    },
    datePublished: '2026-06-01',
    dateModified: '2026-06-25',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  };
}

export function buildArticleBreadcrumbSchema(article: Article): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Articles',
        item: `${siteUrl}/#articles`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `${siteUrl}/article/${article.slug}`,
      },
    ],
  };
}